export const API = "/api/v1";

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(t: string | null) { accessToken = t; }
export function getAccessToken() { return accessToken; }

export class ApiError extends Error {
  constructor(public status: number, public detail: string) { super(detail); }
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" })
      .then(async (r) => { if (!r.ok) { setAccessToken(null); return false; } setAccessToken((await r.json()).access_token); return true; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const r = await fetch(`${API}${path}`, { ...init, headers, credentials: "include" });
  if (r.status === 401 && retry && !path.startsWith("/auth/login")) {
    if (await tryRefresh()) return api<T>(path, init, false);
  }
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).detail ?? detail; } catch { /* ignore */ }
    throw new ApiError(r.status, typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export type Role = { id?: string | null; role: string; brand_id: string | null; store_id: string | null; staff_id?: string | null; can_see_receipt_flags: boolean };
export type Me = { id: string; email: string | null; name: string; lang: string; roles: Role[]; pin_unlocked: boolean };
export type Brand = {
  id: string; slug: string; name: Record<string, string>; domain: string; theme: Record<string, string>;
  default_lang: string; booking_mode: "open" | "membered"; account_required: boolean;
  staff_calendar_visibility: "own" | "all_names" | "all_full"; cutoff_hours_default: number; warning_hours: number[];
  logo_url: string | null; min_notice_hours: number; booking_horizon_days: number; staff_pick_allowed: boolean; waitlist_expiry_days: number;
  messaging_provider: string | null; sender_id: string | null; sms_sender_id: string | null; notify_channel: string; otp_channel: string; reminder_hours_before: number;
  wow_mirror_enabled: boolean; wow_base_url: string | null; wow_username: string | null; wow_password_set: boolean; wow_store_id: string | null; wow_poll_days: number;
  deposit_mode: "off" | "flat" | "percent" | null; deposit_value: string | null; review_enabled: boolean; review_delay_hours: number; review_min_days: number; notification_toggles: Record<string, { email: boolean; messaging: boolean }>; viva_source_code: string | null; checkout_grace_min: number; snooze_minutes: number; snooze_max_total_min: number; pin_after_hours: number;
  stores: { id: string; slug: string; name: Record<string, string>; tz: string; online_booking_enabled: boolean; calendar_from: string; calendar_to: string; biweekly_anchor: string | null }[];
};
export type PublicBrand = Pick<Brand, "id" | "slug" | "name" | "theme" | "default_lang" | "booking_mode" | "account_required" | "stores" | "cutoff_hours_default" | "logo_url" | "min_notice_hours" | "booking_horizon_days" | "staff_pick_allowed" | "waitlist_expiry_days">;
